import moment from 'moment';
import {
  blankLocation,
  blankSelectedEmployee,
  CLICK_ACTIONS,
  DEFAULT_OPTION,
  TASK_ACTION,
  TASK_IEC,
} from '../../utils/Constant';
import {
  ColumnHeaderType,
  EmployeeNameType,
  LeadDetailsType,
  SelectOptionType,
  TaskDetailType,
  TaskLocationDataType,
  TaskNameType,
  TaskStatusType,
  ViewTaskFilterType,
} from '../../utils/Types';
import { blankValueGetter, isAPIFailed, isAPISuccess } from '../../utils/Util';
import { SelectChangeEvent } from '@mui/material';
import { toast } from 'react-toastify';
import { ThunkDispatch } from 'redux-thunk';
import { creteNewTask } from '../../features/taskSlice';

export const VIEW_TASK_HEADER: ColumnHeaderType[] = [
  {
    field: 'taskId',
    name: 'Task Id',
    valueGetter: blankValueGetter,
    clickable: true,
    action: CLICK_ACTIONS.VIEW_TASK,
  },
  {
    field: 'taskName',
    name: 'Task Name',
    valueGetter: blankValueGetter,
  },
  {
    field: 'sourceName',
    name: 'Task Type',
    sortable: true,
    valueGetter: blankValueGetter,
  },
  {
    field: 'assignedToName',
    name: 'Assign To',
    toolTipAfter: 25,
    valueGetter: blankValueGetter,
  },
  {
    field: 'taskStatusName',
    name: 'Status',
    sortable: true,
    valueGetter: blankValueGetter,
  },
];

/**
 * Get filter data from selected filters
 * @param selectedFilter
 * @returns
 */
export const getViewTaskFilterData = (
  selectedFilter: ViewTaskFilterType | undefined
) => {
  const data: any = {};
  if (selectedFilter) {
    data.taskId = selectedFilter?.taskId || null;
    data.taskName = selectedFilter?.taskName?.value || null;
    data.customTaskName = selectedFilter?.customTaskName || null;
    data.source = selectedFilter?.source?.value || null;
    data.assignedToId = selectedFilter?.assignedTo?.value || null;
    data.status = selectedFilter?.status?.value || null;
    data.fromDate = selectedFilter?.fromDate
      ? moment(selectedFilter?.fromDate).format('YYYY-MM-DD')
      : null;
    data.toDate = selectedFilter?.toDate
      ? moment(selectedFilter?.toDate).format('YYYY-MM-DD')
      : null;
    data.zone = selectedFilter?.zone?.value || null;
    data.state = selectedFilter?.state?.value || null;
    data.district = selectedFilter?.district?.value || null;
    data.location = selectedFilter?.city?.value || null;
  }

  return data;
};
/**
 * Find label-value pair array of objects.
 * @param empList
 * @returns SelectOptionType[]
 */
export const getEmployeeNameList = (empList: EmployeeNameType[]) => {
  const names: SelectOptionType[] = [];
  empList?.forEach((item: EmployeeNameType) => {
    names.push({
      value: item.candidateId,
      label: item.employeeName,
    });
  });
  return names;
};
/**
 * Find label-value pair array of objects. Here value is employeeUniqueId.
 * @param empList
 * @returns SelectOptionType[]
 */
export const getEmployeeNameListWithUniqueId = (
  empList: EmployeeNameType[]
) => {
  const names: SelectOptionType[] = [];
  empList?.forEach((item: EmployeeNameType) => {
    names.push({
      value: item.employeeUniqueId,
      label: item.employeeName,
    });
  });
  return names;
};

/**
 * Find label-value pair array of objects for task name and task source.
 * @param nameList
 * @param mode
 * @returns SelectOptionType[]
 */
export const findTaskNameAndSourceOptions = (
  nameList: TaskNameType[],
  mode?: string
) => {
  const names: SelectOptionType[] = [];
  const source: SelectOptionType[] = [];
  const sourceTemp: number[] = [];
  nameList?.forEach((item: TaskNameType) => {
    if (item?.isShowInCreate || mode !== TASK_ACTION.CREATE) {
      names.push({
        value: item.taskNameId,
        label: item.taskName,
      });
      if (!sourceTemp.includes(item.taskSourceId)) {
        sourceTemp.push(item.taskSourceId);
        source.push({
          value: item.taskSourceId,
          label: item.sourceName,
        });
      }
    }
  });

  return { taskNames: names, source };
};

/**
 * Find label-value pair array of objects of task status.
 * @param list
 * @returns SelectOptionType[]
 */
export const findTaskStatusOptions = (list: TaskStatusType[]) => {
  const array: SelectOptionType[] = [];
  list?.forEach((item: TaskStatusType) => {
    array.push({
      value: item.statusId,
      label: item.statusName,
    });
  });
  return array;
};
/**
 * Check status of selected/entered fields. If all valid then isValid will be true otherwise false.
 * @param selectedTaskNameId
 * @param selectedSource
 * @param locationData
 * @returns isValid and error message
 */
export const checkValidation = (
  selectedTaskNameId: number | undefined,
  selectedSource: number | undefined,
  locationData: TaskLocationDataType,
  typeOfActivity?: number | string,
  customTaskName?: string | undefined | null,
  inputValue?: string | undefined | null,
  zoneValRequired?: boolean,
  stateValRequired?: boolean,
  districtValRequired?: boolean,
  selectedEmployee?: EmployeeNameType
) => {
  let isValid = true;
  let errMessage = '';
  if (!selectedTaskNameId) {
    errMessage = 'Select task name';
    isValid = false;
  } else if (!selectedSource) {
    errMessage = 'Please select source';
    isValid = false;
  } else if (
    (!locationData?.pincode &&
      selectedTaskNameId &&
      selectedTaskNameId != 13) ||
    (!locationData?.pincode &&
      selectedTaskNameId &&
      selectedTaskNameId == 13 &&
      selectedEmployee?.employeeName)
  ) {
    errMessage = 'Pincode can not be empty.';
    isValid = false;
  } else if (
    (locationData?.pincode &&
      inputValue &&
      locationData?.pincode != inputValue) ||
    (locationData?.pincode &&
      inputValue?.length == 0 &&
      locationData?.pincode != inputValue)
  ) {
    errMessage = 'Selected and entered pincodes do not match.';
    isValid = false;
  } else if (!locationData?.zone) {
    errMessage = 'Zone name can not be empty';
    isValid = false;
  } else if (!locationData?.state) {
    errMessage = 'State name can not be empty';
    isValid = false;
  } else if (!locationData?.district) {
    errMessage = 'District name can not be empty';
    isValid = false;
  } else if (!locationData?.city) {
    errMessage = 'Village name can not be empty';
    isValid = false;
  } else if (
    selectedTaskNameId &&
    selectedTaskNameId === 13 &&
    !customTaskName
  ) {
    errMessage = 'Custom Task name can not be empty';
    isValid = false;
  } else if (
    selectedTaskNameId &&
    selectedTaskNameId == 13 &&
    zoneValRequired &&
    !locationData?.zone
  ) {
    errMessage = 'Please select zone';
    isValid = false;
  } else if (
    selectedTaskNameId &&
    selectedTaskNameId == 13 &&
    stateValRequired &&
    !locationData?.state
  ) {
    errMessage = 'Please select state';
    isValid = false;
  } else if (
    selectedTaskNameId &&
    selectedTaskNameId == 13 &&
    districtValRequired &&
    !locationData?.district
  ) {
    errMessage = 'Please select district';
    isValid = false;
  }
  if (typeOfActivity) {
    console.log('typeOfActivity');
  }
  //  else if (selectedTaskNameId === TASK_IEC && !typeOfActivity) {
  //     errMessage = 'Select type of activity';
  //     isValid = false;
  //   }
  return { isValid, errMessage };
};

export const onTaskNameChangeViewTask = (
  taskOption: SelectOptionType,
  taskNameList: TaskNameType[],
  viewTaskFilter: ViewTaskFilterType,
  setViewTaskFilter: (value: ViewTaskFilterType) => void
) => {
  if (taskNameList && taskOption?.value) {
    for (let index = 0; index < taskNameList?.length; index++) {
      if (taskNameList[index].taskNameId === taskOption.value) {
        setViewTaskFilter({
          ...viewTaskFilter,
          source: {
            value: taskNameList[index].taskSourceId,
            label: taskNameList[index].sourceName,
          },
          taskName: taskOption,
          customTaskName:
            taskOption?.value != 13 ? '' : viewTaskFilter.customTaskName,
        });
        break;
      }
    }
  } else {
    setViewTaskFilter({
      ...viewTaskFilter,
      source: DEFAULT_OPTION,
      taskName: DEFAULT_OPTION,
    });
  }
};
export const onTaskNameChange = (
  selectedName: SelectChangeEvent,
  taskNameList: TaskNameType[],
  setSelectedTaskNameId: (value: number) => void,
  setSelectedSource: (value: number) => void
) => {
  const value = Number(selectedName.target.value);
  setSelectedTaskNameId(value);
  if (taskNameList) {
    for (const element of taskNameList) {
      if (value === element.taskNameId) {
        setSelectedSource(element.taskSourceId);
        break;
      }
    }
  }
};

export const getCreateTaskParams = (
  selectedTaskNameId: number | undefined,
  selectedSource: number | undefined,
  selectedEmployee: EmployeeNameType,
  locationData: TaskLocationDataType,
  completionDateTime: string,
  typeOfActivity: number | string | undefined,
  comment: string | undefined,
  customTaskName: string | undefined | null,
  selectedRolesData: number[] | undefined
) => {
  const data: any = {
    taskNameId: selectedTaskNameId,
    taskSourceId: selectedSource,
    assignedToId: selectedEmployee?.candidateId || null, // if we use ?? then candidateId is '' then assignedToId become '' it should be null
    customerId: null,
    taskLinkId: '',
    taskApplicationId: '',
    policyId: '',
    location: locationData.sl,
    state: locationData.state == 'All' ? Number(0) : Number(locationData.state),
    district:
      locationData.district == 'All'
        ? Number(0)
        : Number(locationData.district),
    city: locationData.city == 'All' ? Number(0) : Number(locationData.city),
    zone: locationData.zone == 'All' ? Number(0) : Number(locationData.zone),
    pincode:
      locationData.pincode == '' &&
      selectedTaskNameId &&
      selectedTaskNameId === 13
        ? 0
        : locationData.pincode,
    completedOn: completionDateTime
      ? new Date(
          moment(completionDateTime, 'DD-MM-YYYY H:mm').format(
            'DD MMM YYYY H:mm'
          )
        ).toISOString()
      : new Date().toISOString(),
    comments: comment ?? '',
    customTaskName:
      customTaskName?.length && customTaskName?.length > 0
        ? customTaskName
        : null,
    roles:
      selectedRolesData?.includes(6) && !selectedRolesData?.includes(38)
        ? [...(selectedRolesData || []), 38]
        : selectedRolesData || [],
  };
  if (selectedTaskNameId && selectedTaskNameId === TASK_IEC && typeOfActivity) {
    data.typeOfActivity = typeOfActivity;
  }
  return data;
};
export const getEmployee = (taskDetails: TaskDetailType | LeadDetailsType) => {
  return {
    employeeName: taskDetails.assignedToName,
    roleName: taskDetails.assignedToRoleName,
    roleId: taskDetails.assignedToRoleID,
    mobileNumber: taskDetails.assignedToNumber,
    employeeUniqueId: taskDetails.assignedToEmployeeUniqueId,
    candidateId: taskDetails.assignedToId,
  };
};
export const getLocation = (taskDetails: TaskDetailType | LeadDetailsType) => {
  return {
    ...blankLocation,
    zone: taskDetails.zone,
    state: taskDetails.state,
    district: taskDetails.district,
    city: taskDetails.city,
    pincode: taskDetails.pincode,
  };
};
export const getDefaultLocation = (
  taskDetails: TaskDetailType | LeadDetailsType
) => {
  return {
    zoneOption: [{ value: taskDetails.zone, label: taskDetails.zoneName }],
    stateOption: [{ value: taskDetails.state, label: taskDetails.stateName }],
    districtOption: [
      { value: taskDetails.district, label: taskDetails.districtName },
    ],
    cityOption: [
      {
        value: taskDetails.city,
        label: taskDetails?.locationName
          ? taskDetails.locationName
          : taskDetails.cityName,
      },
    ],
  };
};
export const updateSelectedEmployee = (
  value: SelectOptionType | undefined,
  employeeList: EmployeeNameType[] | undefined,
  setSelectedEmployee: (value: EmployeeNameType) => void
) => {
  if (employeeList && value) {
    for (const element of employeeList) {
      if (Number(element.candidateId) === value.value) {
        setSelectedEmployee(element);
        break;
      }
    }
  } else {
    setSelectedEmployee(blankSelectedEmployee);
  }
};

export const createTask = (
  data: {
    selectedTaskNameId?: number;
    selectedSource?: number;
    selectedEmployee: EmployeeNameType;
    locationData: TaskLocationDataType;
    completionDateTime: string;
    typeOfActivity?: number | string;
    comment?: string;
    customTaskName?: string | null;
    selectedRolesData?: number[];
    inputValue?: string | null;
    zoneValRequired?: boolean;
    stateValRequired?: boolean;
    districtValRequired?: boolean;
  },
  setShowLoader: (param: boolean) => void,
  dispatch: ThunkDispatch<any, any, any>,
  clearFields: () => void
) => {
  const validation = checkValidation(
    data.selectedTaskNameId,
    data.selectedSource,
    data.locationData,
    data.typeOfActivity,
    data.customTaskName,
    data.inputValue,
    data.zoneValRequired,
    data.stateValRequired,
    data.districtValRequired,
    data.selectedEmployee
  );
  if (!validation.isValid) {
    toast.error(validation.errMessage);
    return;
  }

  setShowLoader(true);
  dispatch(
    creteNewTask(
      getCreateTaskParams(
        data.selectedTaskNameId,
        data.selectedSource,
        data.selectedEmployee,
        data.locationData,
        data.completionDateTime,
        data.typeOfActivity,
        data.comment,
        data.customTaskName,
        data.selectedRolesData
      )
    )
  )
    ?.then((fulfilled: any) => {
      if (isAPIFailed(fulfilled?.payload)) {
        toast.error(fulfilled?.payload.responseInformation.responseMessage);
      } else if (isAPISuccess(fulfilled?.payload)) {
        toast.success(fulfilled?.payload.responseInformation.responseMessage);
        clearFields();
      }
      setShowLoader(false);
    })
    ?.catch(() => {
      setShowLoader(false);
    });
};
